import React from 'react';
import './ChatSourcesPanel.css';

// assets
import downloadIcon from '../../assets/new-images/download-icon.svg';
import diagonalArrow from '../../assets/new-images/diagonal-arrow.svg';
import fileIcon from '../../assets/new-images/file-icon.svg';
import gridIcon from '../../assets/new-images/docs-icon.svg';
import globeIcon from '../../assets/new-images/blue-globe-icon.svg';

/**
 * ChatSourcesPanel
 *
 * A reusable panel that renders source citations in three flavours:
 *   • "transcript" — interview / document files  (file icon · download button)
 *   • "excel"      — spreadsheet / xlsx files    (grid icon  · arrow link)
 *   • "web"        — external web URLs           (globe icon · arrow link)
 *
 * @param {"transcript"|"excel"|"web"|string} type   Source flavour. Defaults to "web".
 * @param {string}   [title]        Override the auto-generated section heading.
 * @param {Array}    sources        Array of source items: { label, href, date, download, onClick }
 * @param {string}   [footer]       Footer text. If omitted, auto-built from item.date fields.
 * @param {Function} [onItemClick]  Handler called with (item, index) on row click.
 * @param {number}   [collapseAfter=5]  Max rows shown before the list becomes scrollable.
 * @param {string}   [className]    Extra class names on the root element.
 */

const TYPE_CONFIG = {
    transcript: { heading: 'TRANSCRIPT CITATIONS', icon: fileIcon },
    excel: { heading: 'EXCEL SOURCES', icon: gridIcon },
    web: { heading: 'SOURCES', icon: globeIcon },
};

// ── Date helpers ─────────────────────────────────────────────────────────────
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** Extract the raw date string from a source item */
const getItemDate = (item) =>
    item.date || item.refresh_date || item.retrieved_date ||
    item.last_modified || item.updated_at || item.timestamp || '';

/**
 * Parse any date string (ISO or already-formatted) into a JS Date.
 * Returns null when unparseable.
 */
const parseDate = (str) => {
    if (!str) return null;
    const d = new Date(str);
    return isNaN(d.getTime()) ? null : d;
};

/** "02 Mar" — no year */
const fmtShort = (d) =>
    `${String(d.getDate()).padStart(2, '0')} ${MONTHS[d.getMonth()]}`;

/** "02 Mar 2026" — with year */
const fmtFull = (d) => `${fmtShort(d)} ${d.getFullYear()}`;

/** Build footer text automatically from source item dates */
const computeAutoFooter = (sources, type) => {
    if (type === 'transcript') {
        const dates = sources
            .map((s) => parseDate(getItemDate(s)))
            .filter(Boolean)
            .sort((a, b) => a - b);       // chronological order

        if (!dates.length) return '';

        if (dates.length === 1) {
            return `As of - interviews conducted ${fmtFull(dates[0])}`;
        }

        const first = dates[0];
        const last = dates[dates.length - 1];

        // Same year → "24 Feb – 06 Jun 2026"
        // Diff year → "24 Feb 2025 – 06 Jun 2026"
        const firstStr = first.getFullYear() === last.getFullYear()
            ? fmtShort(first)
            : fmtFull(first);

        return `As of - interviews conducted ${firstStr} – ${fmtFull(last)}`;
    }

    if (type === 'excel') {
        const parts = sources
            .filter((s) => getItemDate(s))
            .map((s) => {
                const d = parseDate(getItemDate(s));
                return `${s.label} refreshed ${d ? fmtFull(d) : getItemDate(s)}`;
            });
        return parts.length ? `As of — ${parts.join(' · ')}` : '';
    }

    if (type === 'web') {
        const dates = sources
            .map((s) => parseDate(getItemDate(s)))
            .filter(Boolean)
            .sort((a, b) => a - b);
        return dates.length ? `Retrieved ${fmtFull(dates[dates.length - 1])}` : '';
    }

    return '';
};


// ── Component ────────────────────────────────────────────────────────────────
const ChatSourcesPanel = ({
    type = 'web',
    title,
    sources = [],
    footer,
    onItemClick,
    collapseAfter = 3,
    className = '',
}) => {
    if (!sources || sources.length === 0) return null;

    const config = TYPE_CONFIG[type] || TYPE_CONFIG.web;
    const heading = title || config.heading;
    const icon = config.icon;
    const isTranscript = type === 'transcript';

    // When there are more items than the threshold, make the list scrollable
    const needsScroll = sources.length > collapseAfter;

    // footer prop takes priority; otherwise auto-compute from item dates
    const displayFooter = footer !== undefined ? footer : computeAutoFooter(sources, type);

    const handleClick = (e, item, idx) => {
        const handler = onItemClick ? () => onItemClick(item, idx) : item.onClick;
        if (handler) {
            e.preventDefault();
            handler(item, idx);
        } else if (item.href && item.href !== '#') {
            e.preventDefault();
            window.open(item.href, '_blank', 'noopener,noreferrer');
        }
    };

    return (
        <div className={`chat-sources-panel chat-sources-panel-${type} ${className}`.trim()}>

            {/* ── Heading ── */}
            <div className="chat-sources-panel-heading">{heading}</div>

            {/* ── Source rows (scrollable when > collapseAfter items) ── */}
            <ul className={`chat-sources-panel-list${needsScroll ? ' chat-sources-panel-list--scroll thin-scroll' : ''}`}>
                {sources.map((item, idx) => (
                    <li
                        key={idx}
                        className="panel-item"
                        onClick={(e) => handleClick(e, item, idx)}
                    >

                        {/* Left: type icon + label */}
                        <div className="panel-item-left">
                            <div className="panel-item-icon">
                                <img src={icon} alt="" />
                            </div>
                            <div className="panel-item-label" title={item.label}>
                                {item.label}
                            </div>
                        </div>

                        {/* Right: download (transcript) or arrow (web) */}
                        <a
                            href={item.href || '#'}
                            target={item.href && item.href !== '#' ? '_blank' : undefined}
                            rel="noopener noreferrer"
                            className="panel-item-action button-style"
                            aria-label={`${isTranscript ? 'Download' : 'Open'} ${item.label}`}
                            download={item.download || undefined}
                            onClick={(e) => e.preventDefault()}
                        >
                            {isTranscript
                                ? <img src={downloadIcon} alt="" />
                                : <img src={diagonalArrow} alt="" />
                            }
                        </a>
                    </li>
                ))}
            </ul>

            {/* ── Footer ── */}
            {displayFooter && (
                <div className="chat-sources-panel-footer">{displayFooter}</div>
            )}
        </div>
    );
};

export default ChatSourcesPanel;
